/**
 * Fromas Cloud Support Chat
 * Enterprise-grade Chat Component with RAG Workflow
 * Created by: Newso Technologies Consultants Co., Ltd.
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Send, X, Loader2, FileText, AlertCircle, BookOpen } from 'lucide-react';
import Icon from '@mdi/react';
import { mdiFaceAgent, mdiBookOpen } from '@mdi/js';
import './styles.css';
import { ChatService } from '../../services/chatService';
import { Message } from '../../types/chat.types';

interface FromasChatAssistantProps {
  className?: string;
  position?: 'bottom-right' | 'bottom-left';
  showFloatingButton?: boolean;
  onClose?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const FromasChatAssistant: React.FC<FromasChatAssistantProps> = ({
  className = '',
  position = 'bottom-right',
  showFloatingButton = true,
  onClose,
  open: externalOpen,
  onOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'สวัสดีครับ! 👋\n\nยินดีต้อนรับสู่ Fromas Cloud Support ครับ ผมพร้อมช่วยตอบคำถามเกี่ยวกับระบบ Fromas Cloud ให้คุณได้เลย\n\n💡 คำแนะนำ:\n• ถามเกี่ยวกับการใช้งานระบบ เช่น "วิธีจองห้องพัก" หรือ "วิธีเช็คอิน"\n• ถามเกี่ยวกับรายงานหรือฟีเจอร์ต่างๆ\n• ผมจะค้นหาข้อมูลจากคู่มือการใช้งานอย่างเป็นทางการให้คุณ\n\nมีอะไรให้ผมช่วยไหมครับ? 😊',
      timestamp: new Date(),
      metadata: {
        type: 'greeting'
      }
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [typingBuffer, setTypingBuffer] = useState<string>('');
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatService = useRef(new ChatService());
  const isUserAtBottomRef = useRef(true);
  const shouldAutoScrollRef = useRef(true);

  // Debug logging
  useEffect(() => {
  }, [position]);

  // Smooth streaming effect - update content smoothly without aggressive scrolling
  useEffect(() => {
    if (typingBuffer && typingBuffer.length > 0) {
      // Find the streaming message
      const streamingMessage = messages.find(msg => msg.isStreaming && msg.role === 'assistant');
      if (streamingMessage) {
        // Clear any existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        const targetContent = typingBuffer;
        const currentContent = streamingMessage.content || '';

        // Only update if there's new content (chunk-based, not char-by-char)
        if (targetContent.length > currentContent.length) {
          // Update in chunks for smoother rendering
          const chunkSize = Math.max(10, Math.floor((targetContent.length - currentContent.length) / 3));
          const newContent = targetContent.substring(0, Math.min(currentContent.length + chunkSize, targetContent.length));

          setMessages(prev => prev.map(msg => {
            if (msg.id === streamingMessage.id) {
              return {
                ...msg,
                content: newContent
              };
            }
            return msg;
          }));

          // Check if user scrolled up - if so, don't auto-scroll
          const isAtBottom = checkIfUserAtBottom();
          if (isAtBottom && shouldAutoScrollRef.current) {
            // Smooth scroll only if user is at bottom
            requestAnimationFrame(() => {
              if (messagesEndRef.current && isUserAtBottomRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }
            });
          }

          // Continue updating if there's more content
          if (newContent.length < targetContent.length) {
            typingTimeoutRef.current = setTimeout(() => {
              // Trigger re-render to continue streaming
              setTypingBuffer(targetContent);
            }, 50); // Smooth chunk update delay
          }
        }
      }
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [typingBuffer, messages]);

  // Check if user is at bottom of messages container
  const checkIfUserAtBottom = () => {
    if (!messagesContainerRef.current) return true;
    const container = messagesContainerRef.current;
    const threshold = 100; // pixels from bottom
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    isUserAtBottomRef.current = isAtBottom;
    return isAtBottom;
  };

  // Handle scroll events
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      checkIfUserAtBottom();
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto scroll to bottom only if user is at bottom
  useEffect(() => {
    if (shouldAutoScrollRef.current && isUserAtBottomRef.current && messagesEndRef.current) {
      // Use requestAnimationFrame for smooth scrolling
      requestAnimationFrame(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    }
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;


    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const query = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    setError(null);

    // Create assistant message for streaming
    const assistantId = `assistant-${Date.now()}`;
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      images: [],
      isStreaming: true
    };

    setMessages(prev => [...prev, assistantMessage]);
    setTypingBuffer(''); // Reset typing buffer
    shouldAutoScrollRef.current = true; // Allow auto-scroll for new message

    try {

      // Use streaming method
      await chatService.current.sendMessageStream(query, (status) => {
        setMessages(prev => prev.map(msg => {
          if (msg.id === assistantId) {
            const updated: Message = { ...msg };

            switch (status.type) {
              case 'status':
                // Don't show status card, just silently process
                break;

              case 'text':
                // Smooth streaming: accumulate text in buffer
                const newText = status.text || '';
                setTypingBuffer(prev => {
                  const newBuffer = prev + newText;
                  // Check if user scrolled up - if so, disable auto-scroll
                  if (messagesContainerRef.current) {
                    const container = messagesContainerRef.current;
                    const threshold = 100;
                    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
                    if (!isAtBottom) {
                      shouldAutoScrollRef.current = false;
                      isUserAtBottomRef.current = false;
                    }
                  }
                  return newBuffer;
                });
                break;

              case 'image':
                // Add image progressively
                if (status.image && !updated.images?.some(img => img.page === status.image!.page)) {
                  updated.images = [...(updated.images || []), status.image];
                }
                break;

              case 'complete':
                // Final result - flush any remaining buffer
                if (status.result) {
                  updated.content = typingBuffer || status.result.output_text || updated.content;
                  updated.images = status.result.images || updated.images;
                  updated.metadata = {
                    has_images: status.result.has_images,
                    images_processed: status.result.images_processed,
                    cost_estimate: status.result.cost_estimate,
                    processing_time: status.result.processing_time,
                    sources: status.result.sources?.map((s: any) => ({
                      document_id: s.filename,
                      chunk_text: s.heading,
                      page_number: 0,
                      similarity_score: s.score,
                    })),
                  };
                } else {
                  updated.content = typingBuffer || updated.content;
                }
                updated.isStreaming = false;
                setTypingBuffer(''); // Clear buffer
                // Re-enable auto-scroll when complete if user is at bottom
                if (isUserAtBottomRef.current) {
                  shouldAutoScrollRef.current = true;
                  setTimeout(() => {
                    if (messagesEndRef.current && isUserAtBottomRef.current) {
                      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
                    }
                  }, 100);
                }
                break;

              case 'error':
                updated.content = status.message || 'ขอโทษครับ เกิดข้อผิดพลาดในการตอบคำถาม\nกรุณาลองใหม่อีกครั้งครับ';
                updated.isStreaming = false;
                updated.metadata = {
                  type: 'error'
                };
                setTypingBuffer(''); // Clear buffer on error
                break;
            }

            return updated;
          }
          return msg;
        }));
      });

    } catch (err: any) {
      console.error('❌ Chat error:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');

      setMessages(prev => prev.map(msg => {
        if (msg.id === assistantId) {
          return {
            ...msg,
            content: 'ขอโทษครับ 🙏\n\nเกิดข้อผิดพลาดในการตอบคำถามของคุณ กรุณาลองใหม่อีกครั้งครับ\n\n💡 คำแนะนำ:\n• ตรวจสอบการเชื่อมต่ออินเทอร์เน็ต\n• ลองถามคำถามใหม่ในรูปแบบที่ชัดเจนขึ้น\n• ถ้ายังมีปัญหา กรุณาติดต่อทีมสนับสนุนครับ',
            status: '',
            isStreaming: false,
            metadata: {
              type: 'error'
            }
          };
        }
        return msg;
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleOpen = () => {
    if (onOpenChange) {
      onOpenChange(true);
    } else {
      setInternalOpen(true);
    }
  };

  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      setInternalOpen(false);
    }
    if (onClose) {
      onClose();
    }
  };

  // Inline styles สำหรับ floating button
  const buttonStyles: React.CSSProperties = {
    position: 'fixed',
    [position === 'bottom-right' ? 'right' : 'left']: '24px',
    bottom: '24px',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 25px rgba(37, 99, 235, 0.4)',
    zIndex: 9999,
    transition: 'all 0.3s ease',
  };

  const buttonHoverStyles: React.CSSProperties = {
    transform: 'scale(1.1) translateY(-2px)',
    boxShadow: '0 15px 35px rgba(37, 99, 235, 0.5)',
  };

  const pulseIndicatorStyles: React.CSSProperties = {
    position: 'absolute',
    top: '-3px',
    right: '-3px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#EF4444',
    border: '2px solid white',
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  };

  const overlayStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 9998,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  };

  const modalStyles: React.CSSProperties = {
    position: 'relative',
    background: '#FFFFFF',
    borderRadius: '0',
    boxShadow: 'none',
    width: '100%',
    maxWidth: '640px',
    height: '85vh',
    maxHeight: '800px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid #E3E5E6',
  };

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    background: '#FFFFFF',
    borderBottom: '1px solid #E3E5E6',
    boxShadow: 'none',
    width: '100%',
    minHeight: '44px',
  };

  const messagesContainerStyles: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '0px',
    background: '#FFFFFF',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
    position: 'relative',
    width: '100%',
  };

  const inputContainerStyles: React.CSSProperties = {
    padding: '16px 24px',
    background: '#FFFFFF',
    borderTop: '1px solid #E3E5E6',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const inputStyles: React.CSSProperties = {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '24px',
    border: '1px solid #E3E5E6',
    fontSize: '16px',
    lineHeight: '24px',
    outline: 'none',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
    transition: 'all 0.3s ease',
    backgroundColor: '#F2F4F5',
    color: '#303437',
  };

  const sendButtonStyles: React.CSSProperties = {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: 'none',
    background: inputValue.trim() && !isLoading ? '#0070F0' : '#E3E5E6',
    color: 'white',
    cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '8px',
    transition: 'all 0.3s ease',
    boxShadow: 'none',
  };

  const [isHovering, setIsHovering] = useState(false);

  return (
    <>
      {/* Floating Chat Icon Button */}
      {showFloatingButton && (
        <button
          onClick={handleOpen}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          style={{
            ...buttonStyles,
            ...(isHovering ? buttonHoverStyles : {})
          }}
          title="Chat with Fromas Support"
          aria-label="Open Fromas Support Chat"
          className={className}
        >
          <Icon
            path={mdiFaceAgent}
            size={1.4}
            style={{
              color: 'white',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
            }}
          />
          <span style={pulseIndicatorStyles} />
        </button>
      )}

      {/* Modal Overlay */}
      {open && (
        <div
          style={overlayStyles}
        >
          <div
            style={modalStyles}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - FitBot Style */}
            <div style={headerStyles}>
              {/* Left Content - Avatar + Name + Status */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '12px',
                flex: 1,
              }}>
                {/* Avatar */}
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: '#F2F8FF',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon
                    path={mdiFaceAgent}
                    size={1.2}
                    style={{
                      color: '#0070F0',
                    }}
                  />
                </div>
                {/* Name + Activity */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '2px',
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 700,
                    lineHeight: '20px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                    color: '#202325',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    Fromas Support
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: '0px',
                    gap: '4px',
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      background: '#7DDE86',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }} />
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '16px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                      color: '#72777A',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      Always active
                    </span>
                  </div>
                </div>
              </div>
              {/* Right Content - Document Button + Close Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
              }}>
                {/* Document Button */}
                <button
                  onClick={() => {
                    const url = 'https://docs.fromas.cloud/books/fromas-cloud-user-manual/page/a8baa';
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  style={{
                    height: '44px',
                    padding: '0 16px',
                    borderRadius: '22px',
                    border: '1px solid #E3E5E6',
                    background: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F2F4F5';
                    e.currentTarget.style.borderColor = '#0070F0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFFFFF';
                    e.currentTarget.style.borderColor = '#E3E5E6';
                  }}
                  aria-label="Open documentation"
                >
                  <BookOpen style={{
                    width: '18px',
                    height: '18px',
                    color: '#72777A',
                  }} />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#72777A',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                  }}>
                    Document
                  </span>
                </button>
                {/* Close Button */}
                <button
                  onClick={handleClose}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    border: '1px solid #E3E5E6',
                    background: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F2F4F5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFFFFF';
                  }}
                  aria-label="Close chat"
                >
                  <X style={{
                    width: '20px',
                    height: '20px',
                    color: '#72777A',
                  }} />
                </button>
              </div>
            </div>

            {/* Messages Container */}
            <div
              ref={messagesContainerRef}
              style={messagesContainerStyles}
            >
              <div style={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                {messages.map((message, index) => {
                  const isBot = message.role === 'assistant';
                  const isUser = message.role === 'user';
                  const showTimestamp = index === 0 || (index > 0 && messages[index - 1].role !== message.role);

                  return (
                    <div key={message.id} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isUser ? 'flex-end' : 'flex-start',
                      gap: '16px',
                    }}>
                      {/* Message from Bot/User */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '0px',
                        gap: '8px',
                        width: '100%',
                      }}>
                        {/* Timestamp */}
                        {showTimestamp && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '16px',
                          }}>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: 500,
                              lineHeight: '16px',
                              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                              color: '#72777A',
                              whiteSpace: 'nowrap',
                            }}>
                              {message.timestamp.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}

                        {/* Message */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          padding: '0px',
                          gap: '8px',
                          width: '100%',
                          justifyContent: isUser ? 'flex-end' : 'flex-start',
                        }}>
                          {/* Bot Avatar */}
                          {isBot && (
                            <div style={{
                              width: '32px',
                              height: '32px',
                              background: '#F2F8FF',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <Icon
                                path={mdiFaceAgent}
                                size={0.8}
                                style={{
                                  color: '#0070F0',
                                }}
                              />
                            </div>
                          )}

                          {/* Message Bubble */}
                          <div style={{
                            maxWidth: isUser ? '85%' : 'calc(85% - 40px)',
                            padding: '16px',
                            gap: '10px',
                            borderRadius: isUser ? '24px 24px 0px 24px' : '0px 24px 24px 24px',
                            background: isUser ? '#0070F0' : '#F2F4F5',
                            color: isUser ? '#FFFFFF' : '#303437',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            flex: isUser ? 'none' : 1,
                            flexGrow: isUser ? 0 : 1,
                            opacity: message.isStreaming && !message.content ? 0.7 : 1,
                            transition: 'opacity 0.3s ease-in-out',
                          }}>
                            {/* Message Content with Smooth Streaming */}
                            {isUser ? (
                              <p style={{
                                margin: 0,
                                fontSize: '15px',
                                lineHeight: '1.6',
                                fontWeight: 400,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                                color: '#FFFFFF',
                                textAlign: 'center',
                                width: '100%',
                                letterSpacing: '0.01em',
                              }}>
                                {message.content}
                              </p>
                            ) : (
                              <div style={{
                                width: '100%',
                                fontSize: '15px',
                                lineHeight: '1.7',
                                color: '#303437',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                                letterSpacing: '0.01em',
                                animation: message.isStreaming && message.content ? 'fadeIn 0.3s ease-in' : 'none',
                              }}>
                                {message.content ? (
                                  <>
                                    <ReactMarkdown
                                      components={{
                                        p: ({ children }: { children: React.ReactNode }) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
                                        h3: ({ children }: { children: React.ReactNode }) => <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '12px 0 4px 0', color: '#1a1a2e' }}>{children}</h3>,
                                        h2: ({ children }: { children: React.ReactNode }) => <h2 style={{ fontSize: '16px', fontWeight: 700, margin: '14px 0 6px 0', color: '#1a1a2e' }}>{children}</h2>,
                                        strong: ({ children }: { children: React.ReactNode }) => <strong style={{ fontWeight: 700, color: '#1a1a2e' }}>{children}</strong>,
                                        ul: ({ children }: { children: React.ReactNode }) => <ul style={{ margin: '4px 0 8px 0', paddingLeft: '20px' }}>{children}</ul>,
                                        ol: ({ children }: { children: React.ReactNode }) => <ol style={{ margin: '4px 0 8px 0', paddingLeft: '20px' }}>{children}</ol>,
                                        li: ({ children }: { children: React.ReactNode }) => <li style={{ margin: '2px 0' }}>{children}</li>,
                                        code: ({ children }: { children: React.ReactNode }) => <code style={{ background: '#f3f4f6', borderRadius: '3px', padding: '1px 5px', fontSize: '13px', fontFamily: 'monospace' }}>{children}</code>,
                                        hr: () => <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '10px 0' }} />,
                                      }}
                                    >
                                      {message.content}
                                    </ReactMarkdown>
                                    {message.isStreaming && (
                                      <span style={{
                                        display: 'inline-block',
                                        width: '2px',
                                        height: '18px',
                                        backgroundColor: '#0070F0',
                                        marginLeft: '2px',
                                        animation: 'blink 1s infinite',
                                        verticalAlign: 'middle',
                                        borderRadius: '1px',
                                      }} />
                                    )}
                                  </>
                                ) : message.isStreaming ? (
                                  <span style={{ color: '#72777A', fontStyle: 'italic', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite', color: '#0070F0' }} />
                                    กำลังพิมพ์...
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Display images from PDF if available */}
                      {message.images && message.images.length > 0 && (
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                          width: '100%',
                        }}>
                          <p style={{
                            margin: '0 0 14px 0',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#4A90E2',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Sukhumvit Set", "Prompt", sans-serif',
                            letterSpacing: '0.02em'
                          }}>
                            <FileText style={{ width: '16px', height: '16px' }} />
                            📸 รูปภาพจากคู่มือ ({message.images.length} หน้า)
                          </p>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                            gap: '12px'
                          }}>
                            {message.images.map((image, idx) => (
                              <div
                                key={idx}
                                style={{
                                  position: 'relative',
                                  borderRadius: '12px',
                                  overflow: 'hidden',
                                  border: '2px solid #E8F4FD',
                                  backgroundColor: '#F9FAFB',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                }}
                                onClick={() => setSelectedImage(image.url)}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.03)';
                                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(74, 144, 226, 0.2)';
                                  e.currentTarget.style.borderColor = '#4A90E2';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.boxShadow = 'none';
                                  e.currentTarget.style.borderColor = '#E8F4FD';
                                }}
                              >
                                <img
                                  src={image.url}
                                  alt={`หน้า ${image.page}`}
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '180px',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                  loading="lazy"
                                />
                                <div style={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  background: 'linear-gradient(to top, rgba(74, 144, 226, 0.9) 0%, rgba(74, 144, 226, 0.7) 50%, transparent 100%)',
                                  padding: '20px 8px 6px 8px',
                                  color: 'white',
                                  fontSize: '12px',
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  fontFamily: '-apple-system, BlinkMacSystemFont, "Sukhumvit Set", sans-serif'
                                }}>
                                  📄 หน้า {image.page}
                                </div>
                              </div>
                            ))}
                          </div>
                          <p style={{
                            margin: '10px 0 0 0',
                            fontSize: '11px',
                            color: '#64748B',
                            textAlign: 'center',
                            fontWeight: '500'
                          }}>
                            คลิกรูปเพื่อดูขนาดเต็ม
                          </p>
                        </div>
                      )}

                      {/* Vision API Metadata */}
                      {message.metadata?.cost_estimate && (
                        <div style={{
                          marginTop: '12px',
                          paddingTop: '12px',
                          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                          fontSize: '11px',
                          color: '#64748B',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '12px',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                          width: '100%',
                        }}>
                          {/* {message.metadata.images_processed && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              📸 <strong>{message.metadata.images_processed}</strong> images
                            </span>
                          )}
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            💰 <strong>${message.metadata.cost_estimate.toFixed(4)}</strong>
                          </span>
                          {message.metadata.processing_time && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              ⏱️ <strong>{(message.metadata.processing_time / 1000).toFixed(1)}s</strong>
                            </span>
                          )} */}
                        </div>
                      )}

                      {/* Show sources if available */}
                      {message.metadata?.sources && message.metadata.sources.length > 0 && (
                        <div style={{
                          marginTop: '12px',
                          paddingTop: '12px',
                          borderTop: '1px solid #e5e7eb',
                          width: '100%',
                        }}>
                          <p style={{
                            margin: '0 0 4px 0',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                          }}>
                            <FileText style={{ width: '12px', height: '12px' }} />
                            แหล่งข้อมูล:
                          </p>
                          {message.metadata.sources.map((source: any, idx: number) => (
                            <p key={idx} style={{
                              margin: '3px 0',
                              fontSize: '11px',
                              color: '#6b7280',
                              paddingLeft: '16px',
                              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                            }}>
                              • <strong style={{ color: '#4b5563' }}>{source.document_id?.replace('.md', '')}</strong>
                              {source.chunk_text ? ` › ${source.chunk_text}` : ''}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Loading Indicator - FitBot Style */}
              {isLoading && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '0px',
                    gap: '8px',
                    width: '100%',
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      padding: '0px',
                      gap: '8px',
                      width: '100%',
                    }}>
                      {/* <div style={{
                        width: '32px',
                        height: '32px',
                        background: '#F2F8FF',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        position: 'relative',
                      }}>
                        <Cloud style={{
                          width: '16px',
                          height: '16px',
                          color: '#0070F0',
                          position: 'absolute',
                          left: '8px',
                          top: '8px',
                        }} />
                      </div> */}
                      {/* <div style={{
                        padding: '16px',
                        gap: '10px',
                        borderRadius: '0px 24px 24px 24px',
                        background: '#F2F4F5',
                        color: '#303437',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        flex: 1,
                        flexGrow: 1,
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                        }}>
                          <Loader2 style={{
                            width: '16px',
                            height: '16px',
                            animation: 'spin 1s linear infinite',
                            color: '#0070F0'
                          }} />
                          <span style={{
                            fontSize: '15px',
                            lineHeight: '1.6',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                            color: '#303437',
                          }}>
                            กำลังค้นหาข้อมูลให้คุณ...
                          </span>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display - FitBot Style */}
              {error && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '0px',
                    gap: '8px',
                    width: '100%',
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      padding: '0px',
                      gap: '8px',
                      width: '100%',
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: '#F2F8FF',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        position: 'relative',
                      }}>
                        <Icon
                          path={mdiFaceAgent}
                          size={0.8}
                          style={{
                            color: '#0070F0',
                            position: 'absolute',
                            left: '8px',
                            top: '8px',
                          }}
                        />
                      </div>
                      <div style={{
                        padding: '16px',
                        gap: '10px',
                        borderRadius: '0px 24px 24px 24px',
                        background: '#F2F4F5',
                        color: '#303437',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        flex: 1,
                        flexGrow: 1,
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          width: '100%',
                        }}>
                          <AlertCircle style={{
                            width: '16px',
                            height: '16px',
                            color: '#dc2626',
                            flexShrink: 0,
                            marginTop: '2px'
                          }} />
                          <p style={{
                            margin: 0,
                            fontSize: '16px',
                            lineHeight: '24px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                            color: '#303437'
                          }}>
                            {error}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area - FitBot Style */}
            <div style={inputContainerStyles}>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
              }}>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0070F0';
                    e.target.style.backgroundColor = '#FFFFFF';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E3E5E6';
                    e.target.style.backgroundColor = '#F2F4F5';
                  }}
                  placeholder="พิมพ์คำถามของคุณที่นี่..."
                  disabled={isLoading}
                  style={{
                    ...inputStyles,
                    opacity: isLoading ? 0.6 : 1,
                    width: '100%',
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  style={sendButtonStyles}
                  onMouseEnter={(e) => {
                    if (inputValue.trim() && !isLoading) {
                      e.currentTarget.style.background = '#006BE6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = inputValue.trim() && !isLoading ? '#0070F0' : '#E3E5E6';
                  }}
                  aria-label="Send message"
                >
                  <Send style={{ width: '22px', height: '22px', color: inputValue.trim() && !isLoading ? '#FFFFFF' : '#72777A' }} />
                </button>
              </div>

              {/* Footer Info - FitBot Style */}
              <p style={{
                margin: '0',
                fontSize: '12px',
                textAlign: 'center',
                color: '#72777A',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Sukhumvit Set", "Prompt", "Kanit", "Noto Sans Thai", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontWeight: 400,
                lineHeight: '1.5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}>
                <Icon
                  path={mdiBookOpen}
                  size={0.9}
                  style={{
                    color: '#72777A',
                    flexShrink: 0,
                  }}
                />
                <span>ข้อมูลจากคู่มือการใช้งาน Fromas Cloud อย่างเป็นทางการ</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal for Full Size View */}
      {selectedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(44, 62, 80, 0.96)',
            backdropFilter: 'blur(12px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            cursor: 'pointer',
          }}
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(74, 144, 226, 0.2)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: '300',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(74, 144, 226, 0.4)';
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(74, 144, 226, 0.2)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
            aria-label="Close image"
          >
            <X style={{ width: '24px', height: '24px' }} />
          </button>
          <img
            src={selectedImage}
            alt="Full size preview"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '16px',
              boxShadow: '0 12px 48px rgba(74, 144, 226, 0.3)',
              border: '3px solid rgba(255, 255, 255, 0.2)',
              cursor: 'default',
            }}
          />
        </div>
      )}

      {/* Keyframes for animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes blink {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default FromasChatAssistant;
